import React, { FC, useState, useRef, useCallback, FocusEvent } from 'react';

import { ReactComponent as VisibilityIcon } from '../../../icons/visibility.svg';
import { ReactComponent as VisibilityOffIcon } from '../../../icons/visibilityOff.svg';
import TextInput, { TextInputProps } from '../TextInput';
import useTranslation from '../../../hooks/useTranslation';

import { StyledVisibilityButton } from './PasswordInput.styles';

export interface PasswordInputProps extends TextInputProps {
  /** Hide the password when blurring the field. */
  hidePasswordOnBlur?: boolean;
}

export interface PasswordInputStatics {
  labeledControl: boolean;
}

const PasswordInput: FC<PasswordInputProps> & PasswordInputStatics = ({ disabled, readOnly, hidePasswordOnBlur, onBlur, ...props }) => {
  const { t } = useTranslation('Core.PasswordInput');
  const inputRef = useRef<HTMLInputElement>();
  const [showPassword, setShowPassword] = useState(false);

  const handleClick = useCallback(() => {
    setShowPassword(!showPassword);
    inputRef.current.focus();
    const caretPosition = inputRef.current.value.length;
    setTimeout(() => {
      inputRef.current.setSelectionRange(caretPosition, caretPosition);
    });
  }, [setShowPassword, showPassword]);

  const handleBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
    if (hidePasswordOnBlur && showPassword) setShowPassword(false);
    if (onBlur) onBlur(event);
  }, [hidePasswordOnBlur, onBlur, showPassword]);

  return (
    <TextInput
      {...props}
      inputRef={inputRef}
      disabled={disabled || readOnly}
      readOnly={readOnly}
      type={showPassword ? 'text' : 'password'}
      clickableRightSign
      rightSign={
        <StyledVisibilityButton
          title={t('visibilityButtonTitle')}
          type="button"
          disabled={disabled || readOnly}
          readOnly={readOnly}
          data-testid="PasswordInputVisibilityButton"
          onClick={handleClick}
        >
          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </StyledVisibilityButton>
      }
      onBlur={handleBlur}
    />
  );
};

PasswordInput.labeledControl = true;

export default PasswordInput;
